import { Component, HostListener, Inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, startWith, take, takeUntil } from 'rxjs/operators';

import { dispatchAppError, setTitle } from 'src/app/actions/core.actions';
import { setBrowserTitle } from 'src/app/actions/core.actions';
import { findStaticData, initializeStaticDataState } from 'src/app/actions/static-data.actions';
import { findProblemImages } from 'src/app/actions/kakomon-image-search.actions';
import { RootState } from 'src/app/reducers';
import { getKakomonImage } from 'src/app/selectors/kakomon-image-search.selector';
import { getFetchedDate } from 'src/app/selectors/static-data.selectors';
import { getUniversities } from 'src/app/selectors/static-data.selectors';

import { STATIC_DATA_CACHE_DAYS, SUBJECT_NAMES } from 'src/app/resources/config';
import { environment } from 'src/environments/environment';
import { ImageType } from 'src/app/models/kakomon-image-search';
import { ProblemImageUtil } from 'src/app/utils/problem-image-util';
import { Log } from 'src/app/utils/log';
import { Dates } from 'src/app/utils/dates';
import { WINDOW_OBJECT } from 'src/app/utils/injection-tokens';
import { RoutingPathResolver } from 'src/app/app-routing-path-resolver';
import { KakomonImageError } from '../../../errors/kakomon-image-error';
import { KAKOMON_ALLOW_CODE_LIST } from '../../../resources/kakomon-allow-code';

@Component({
  selector: 'app-kakomon-image',
  templateUrl: './kakomon-image.component.html',
  styleUrls: ['./kakomon-image.component.scss']
})
export class KakomonImageComponent implements OnInit, OnDestroy {
  private LOG_SOURCE = this.constructor.name;
  private title = '';

  private destroy$ = new Subject<void>();

  loaded$: Observable<boolean>;
  paramType$: Observable<ImageType>;
  questionImages$: Observable<string[]>;
  answerImages$: Observable<string[]>;
  fImages$: Observable<string[]>;
  isError$: Observable<boolean>;
  subjectId: string;

  constructor(private store: Store<RootState>, private route: ActivatedRoute, @Inject(WINDOW_OBJECT) private window: Window) {}

  /**
   * ページ全体で右クリックを無効化し、画像の保存操作を抑止する。
   * ホスト要素だけだと画像外の余白（f 画像などで顕著）が対象外になるため、
   * document で待ち受けて画面全体をカバーする。本コンポーネントは kakomon ルート
   * 専用で、離脱時に破棄されリスナーも外れるため実質このページのみのスコープ。
   * （開発者ツール等では回避可能なため、あくまで気軽な保存の抑止レベル）
   */
  @HostListener('document:contextmenu', ['$event'])
  onContextMenu(event: MouseEvent): void {
    event.preventDefault();
  }

  ngOnInit() {
    this.setUp();

    const params$ = this.route.queryParams.pipe(filter(it => it != null));
    const universities$ = this.store.select(getUniversities).pipe(filter(it => it != null));
    combineLatest([universities$, params$])
      .pipe(
        filter(it => it != null),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(([universities, params]) => {
        // コードのチェック
        if (!KAKOMON_ALLOW_CODE_LIST.includes(params.code)) {
          const url = RoutingPathResolver.resolveTop();
          this.store.dispatch(
            dispatchAppError({
              source: this.LOG_SOURCE,
              error: KakomonImageError.invalidCode('invalid code')
            })
          );
          return;
        }

        const problemId = params.paperId + params.daimon_Id;
        this.store.dispatch(findProblemImages({ problemId }));

        const universityId = params.paperId.toString().substring(2, 6);
        const university = universities.find(item => item.id === `D${universityId}`);
        this.subjectId = params.paperId.toString().substring(6, 8);
        const subject = SUBJECT_NAMES[params.paperId.toString().substring(6, 8)];
        const imageType = params.type === 'a' ? '解答' : '問題';
        this.title = `${university.name} ${subject} ${imageType}`;
        this.store.dispatch(setBrowserTitle({ subTitle: this.title }));
        setTimeout(() => this.store.dispatch(setTitle({ title: this.title })));
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setUp() {
    this.findStaticDataIfNeeded();

    this.loaded$ = this.store.select(getKakomonImage).pipe(
      map(it => it != null),
      startWith(false)
    );

    this.paramType$ = this.route.queryParams.pipe(
      filter(it => it != null),
      take(1),
      map(params => (params.type === 'a' ? 'a' : params.type === 'f' ? 'f' : 'q') as ImageType)
    );

    this.questionImages$ = this.store.select(getKakomonImage).pipe(
      filter(it => it != null),
      take(1),
      map(kakomonImages => kakomonImages.questionImages.map(image => this.getImagePath(image)))
    );

    this.answerImages$ = this.store.select(getKakomonImage).pipe(
      filter(it => it != null),
      take(1),
      map(kakomonImages => kakomonImages.answerImages.map(image => this.getImagePath(image)))
    );

    this.fImages$ = this.store.select(getKakomonImage).pipe(
      filter(it => it != null),
      take(1),
      map(kakomonImages => (kakomonImages.fImage ? [this.getImagePath(kakomonImages.fImage)] : []))
    );

    this.isError$ = combineLatest([this.paramType$, this.questionImages$, this.answerImages$, this.fImages$]).pipe(
      filter(it => it != null),
      take(1),
      map(
        ([paramType, questionImages, answerImages, fImages]) =>
          (paramType === 'q' && questionImages.length === 0) ||
          (paramType === 'a' && answerImages.length === 0) ||
          (paramType === 'f' && fImages.length === 0)
      )
    );

    this.isError$.pipe(takeUntil(this.destroy$)).subscribe(isError => {
      if (isError) {
        this.store.dispatch(
          dispatchAppError({
            source: this.LOG_SOURCE,
            error: KakomonImageError.invalidProblemId('invalid problem id')
          })
        );
      }
    });
  }

  /**
   * f 画像は画像本来のサイズで表示するため、@2x 画像（2 倍解像度）はそのままだと等倍画像の 2 倍の大きさになる。
   * 実サイズの半分を CSS 上の幅として指定し、等倍画像と同じ大きさで表示する。
   */
  onFImageLoaded(event: Event) {
    const image = event.target as HTMLImageElement;
    if (!image.src.includes('@2x')) return;
    image.style.width = `${image.naturalWidth / 2}px`;
  }

  private getImagePath(image: string): string {
    const path = ProblemImageUtil.getPath(image, false);
    return `${environment.cloudfrontHost}${path}`;
  }

  private findStaticDataIfNeeded() {
    this.store
      .select(getFetchedDate)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(fetchedDate => {
        if (!fetchedDate) {
          Log.debug(this.LOG_SOURCE, `static data が存在していないため取得します`);
          this.store.dispatch(findStaticData());
          return;
        }
        const cacheExpired = Dates.isCachedDateExpired(fetchedDate, STATIC_DATA_CACHE_DAYS);
        if (cacheExpired) {
          Log.debug(this.LOG_SOURCE, `cache 期間を超過したため再度 static data を取得します. fetchedDate: ${fetchedDate}`);
          this.store.dispatch(initializeStaticDataState());
          this.store.dispatch(findStaticData());
          return;
        }
        Log.debug(this.LOG_SOURCE, 'static data が取得済みのため何もしません');
      });
  }
}
